import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const About = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to your backend
    const mailtoLink = `mailto:mikel.smart@smartindustriesllc.com?subject=Contact from ${formData.name}&body=${formData.message}%0D%0A%0D%0AFrom: ${formData.email}`;
    window.location.href = mailtoLink;
    setSubmitStatus('success');
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h2" component="h1" fontWeight="700" gutterBottom>
          About Smart Industries LLC
        </Typography>
        <Typography variant="h5" color="text.secondary">
          Helping you integrate with an interconnected world
        </Typography>
      </Box>

      {/* Mission Section */}
      <Paper elevation={3} sx={{ p: 6, mb: 6, borderLeft: '6px solid #d4af37' }}>
        <Typography variant="h4" component="h2" fontWeight="600" gutterBottom>
          How can we Help?
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Our goal has been to help others integrate with an interconnected world. We believe in 
          offering repair services to reduce waste.
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Our goal is to help you integrate technology seamlessly into your life, prioritizing comfort 
          and practicality. We strongly believe that technological advancements have the power to improve 
          your quality of life when adopted thoughtfully.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This doesn't always entail embracing every new trend. If you ever feel overwhelmed by the variety 
          of options available, we are here to assist you in making informed and strategic decisions.
        </Typography>
      </Paper>

      {/* Contact Info and Form */}
      <Grid container spacing={6}>
        {/* Contact Information */}
        <Grid item xs={12} md={5}>
          <Typography variant="h4" component="h2" fontWeight="600" gutterBottom>
            Get in Touch
          </Typography>
          
          <Box sx={{ mt: 4 }}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 6 }
              }}
            >
              <LocationOnIcon sx={{ fontSize: 40, color: '#0d3b66', mr: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Address
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Orlando, FL
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 6 }
              }}
            >
              <EmailIcon sx={{ fontSize: 40, color: '#0d3b66', mr: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Email
                </Typography>
                <Typography
                  variant="body1"
                  color="primary"
                  component="a"
                  href="mailto:mikel.smart@smartindustriesllc.com"
                  sx={{ textDecoration: 'none' }}
                >
                  mikel.smart@smartindustriesllc.com
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={2}
              sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: 6 }
              }}
            >
              <PhoneIcon sx={{ fontSize: 40, color: '#0d3b66', mr: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Phone
                </Typography>
                <Typography
                  variant="body1"
                  color="primary"
                  component="a"
                  href="tel:561-344-4801"
                  sx={{ textDecoration: 'none' }}
                >
                  561-344-4801
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" fontWeight="600" gutterBottom>
              Send us a Message
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Have a question or want to start a consultation? Fill out the form below and we'll get back to you soon.
            </Typography>

            {submitStatus === 'success' && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Thank you for your message! We'll get back to you soon.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                margin="normal"
                variant="outlined"
              />
              
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                margin="normal"
                variant="outlined"
              />
              
              <TextField
                fullWidth
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                multiline
                rows={6}
                margin="normal"
                variant="outlined"
              />
              
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  mt: 3,
                  backgroundColor: '#0d3b66',
                  '&:hover': { backgroundColor: '#1a5490' },
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                Send Message
              </Button>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default About;
